import React, { useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  AssessmentOutlined,
  Download,
  Refresh,
} from "@mui/icons-material";
import { Base_Uri } from "../../Constant/BaseUri";

const initialFilters = {
  startDate: "",
  endDate: "",
  jobNo: "",
  customerName: "",
  collectionStatus: "",
};

const getToken = () => {
  const stored = sessionStorage.getItem("userData");
  return stored ? JSON.parse(stored) : null;
};

const csvValue = (value) => {
  const text = String(value ?? "");
  return `"${text.replaceAll('"', '""')}"`;
};

const FaultyReportDialog = () => {
  const userData = useSelector(
    (state) => state.loginReducer.updatedState,
  );

  const isAdmin =
    String(userData?.role || "").toLowerCase() === "admin";

  const [open, setOpen] = useState(false);
  const [filters, setFilters] = useState(initialFilters);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({
    totalFaultyQty: 0,
    pendingAdminQty: 0,
    collectedAdminQty: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (!isAdmin) {
    return null;
  }

  const fetchReport = async (nextFilters = filters) => {
    try {
      setLoading(true);
      setError("");

      const params = {
        page: 1,
        limit: 500,
      };

      Object.keys(nextFilters).forEach((key) => {
        if (nextFilters[key]) {
          params[key] = nextFilters[key];
        }
      });

      const response = await axios.get(
        `${Base_Uri}repair-jobs/faulty/report`,
        {
          params,
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        },
      );

      setRows(
        Array.isArray(response?.data?.data)
          ? response.data.data
          : [],
      );
      setSummary(
        response?.data?.summary || {
          totalFaultyQty: 0,
          pendingAdminQty: 0,
          collectedAdminQty: 0,
        },
      );
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message ||
          "Unable to fetch faulty report.",
      );
    } finally {
      setLoading(false);
    }
  };

  const openDialog = async () => {
    setOpen(true);
    await fetchReport(initialFilters);
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const resetFilters = async () => {
    setFilters(initialFilters);
    await fetchReport(initialFilters);
  };

  const exportCsv = () => {
    const headers = [
      "Job No",
      "Customer",
      "Account No",
      "Phone",
      "IMEI",
      "Faulty Part",
      "Faulty SKU",
      "Qty",
      "Reason",
      "Job Status At Fault",
      "Reported At",
      "Replacement",
      "Replacement SKU",
      "Admin Collection",
      "Admin Collected At",
    ];

    const csvRows = rows.map((row) => [
      row?.jobNo,
      row?.customerName,
      row?.accountNo,
      `${row?.phoneMake || ""} ${row?.phoneModel || ""}`.trim(),
      row?.imei,
      row?.faultyProductName,
      row?.faultySku,
      row?.faultyQty,
      row?.reason,
      row?.jobStatusAtFault,
      row?.reportedAt
        ? new Date(row.reportedAt).toLocaleString("en-GB")
        : "",
      row?.replacementProductName,
      row?.replacementSku,
      row?.adminCollectionStatus,
      row?.adminCollectedAt
        ? new Date(row.adminCollectedAt).toLocaleString("en-GB")
        : "",
    ]);

    const csv = [headers, ...csvRows]
      .map((line) => line.map(csvValue).join(","))
      .join("\n");

    const blob = new Blob([csv], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `faulty-items-report-${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<AssessmentOutlined />}
        onClick={openDialog}
        sx={{
          minHeight: 44,
          textTransform: "none",
          borderRadius: 2,
          fontWeight: 600,
        }}
      >
        Faulty Report
      </Button>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="xl"
      >
        <DialogTitle>Faulty Items Report</DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2}>
            {error && <Alert severity="error">{error}</Alert>}

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Start Date"
                  name="startDate"
                  value={filters.startDate}
                  onChange={handleFilterChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="End Date"
                  name="endDate"
                  value={filters.endDate}
                  onChange={handleFilterChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Job No"
                  name="jobNo"
                  value={filters.jobNo}
                  onChange={handleFilterChange}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Customer"
                  name="customerName"
                  value={filters.customerName}
                  onChange={handleFilterChange}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  select
                  size="small"
                  label="Admin Collection"
                  name="collectionStatus"
                  value={filters.collectionStatus}
                  onChange={handleFilterChange}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="Pending">Pending</MenuItem>
                  <MenuItem value="Collected">Collected</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} md={2}>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    onClick={() => fetchReport(filters)}
                    disabled={loading}
                  >
                    Apply
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={resetFilters}
                    disabled={loading}
                    startIcon={<Refresh />}
                  >
                    Reset
                  </Button>
                </Stack>
              </Grid>
            </Grid>

            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip
                label={`Total Faulty Qty: ${summary.totalFaultyQty || 0}`}
              />
              <Chip
                color="warning"
                label={`Waiting Admin: ${summary.pendingAdminQty || 0}`}
              />
              <Chip
                color="success"
                label={`Admin Collected: ${summary.collectedAdminQty || 0}`}
              />
            </Stack>

            <Box display="flex" justifyContent="flex-end">
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={exportCsv}
                disabled={rows.length === 0}
              >
                Export CSV
              </Button>
            </Box>

            <TableContainer sx={{ maxHeight: 520 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Job</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell>Faulty Part</TableCell>
                    <TableCell>Qty</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>Status At Fault</TableCell>
                    <TableCell>Replacement</TableCell>
                    <TableCell>Reported At</TableCell>
                    <TableCell>Admin Collection</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                        <CircularProgress size={30} />
                      </TableCell>
                    </TableRow>
                  ) : rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                        No faulty records found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row) => (
                      <TableRow key={row?.faultyId} hover>
                        <TableCell>{row?.jobNo || "-"}</TableCell>
                        <TableCell>{row?.customerName || "-"}</TableCell>
                        <TableCell>{row?.faultyProductName || "-"}</TableCell>
                        <TableCell>{row?.faultyQty || 0}</TableCell>
                        <TableCell sx={{ maxWidth: 260 }}>
                          {row?.reason || "-"}
                        </TableCell>
                        <TableCell>{row?.jobStatusAtFault || "-"}</TableCell>
                        <TableCell>
                          {row?.replacementProductName || "-"}
                        </TableCell>
                        <TableCell>
                          {row?.reportedAt
                            ? new Date(row.reportedAt).toLocaleString("en-GB")
                            : "-"}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={
                              row?.adminCollectionStatus === "Collected"
                                ? "success"
                                : "warning"
                            }
                            label={row?.adminCollectionStatus || "Pending"}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FaultyReportDialog;
